import {useEffect, useState} from 'react'
import {useParams, useNavigate} from 'react-router'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import SkeletonCards from '@/customComponents/editRideComponents/SkeletonCards.jsx'
import DestinationSection from '@/customComponents/editRideComponents/PageModals/DestinationSection.jsx'
import SmartPickupFlow from '@/customComponents/sharedComponents/smartTime/SmartPickupFlow.jsx'
import CarSection from '@/customComponents/editRideComponents/PageModals/CarSection.jsx'
import PassengerSection from '@/customComponents/editRideComponents/PageModals/PassengerSection.jsx'
import LuggageSection from '@/customComponents/editRideComponents/PageModals/LuggageSection.jsx'
import SpecialRequestPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/SpecialRequestPopup.jsx'
import FlightDelayedPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/FlightDelayedPopup.jsx'
import LostBaggagePopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/LostBaggagePopup.jsx'
import AirportPickupPerksPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/AirportPickupPerksPopup.jsx'
import ComingSoonPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/ComingSoonPopup.jsx'
import SuccessPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/SuccessPopup.jsx'
import PoliciesPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/PoliciesPopup.jsx'
import RouteCard from '@/customComponents/editRideComponents/RouteCard.jsx'
import DateTimeCard from '@/customComponents/editRideComponents/DateTimeCard.jsx'
import {PassengersCard, ChildSeatsCard, LuggageItemsCard, OversizedCard} from '@/customComponents/editRideComponents/SummaryCards.jsx'
import CarDetailsCard from '@/customComponents/editRideComponents/CarDetailsCard.jsx'
import SpecialRequestCard from '@/customComponents/editRideComponents/SpecialRequestCard.jsx'
import PaymentMethodsCard from '@/customComponents/editRideComponents/PaymentMethodsCard.jsx'
import ExclusivePerksBar from '@/customComponents/sharedComponents/ExclusivePerksBar.jsx'
import PopularQuestions, {FaqAccordionList} from '@/customComponents/sharedComponents/PopularQuestions.jsx'
import AirportPerksModal from '@/customComponents/sharedComponents/AirportPerksModal.jsx'
import RideBackButton from '@/customComponents/sharedComponents/RideBackButton.jsx'
import {getFaqsForFlow, FAQ_INLINE_COUNT} from '@/constants/faqs.js'
import {POLICY_CONSTANTS} from '@/constants/booking'
import useConfirmTrip from '@/hooks/useConfirmTrip'
import IdentityIncompleteNotice from '@/customComponents/uiComponents/global/IdentityIncompleteNotice.jsx'

export default function RideConfirm() {
    const {rideId} = useParams()
    const navigate = useNavigate()
    // Confirm-specific hook: useEditTrip plus the Draft rules (own pricing pass, own policy
    // source, price-gating). See useConfirmTrip.
    const e = useConfirmTrip(rideId)
    const isReturnFlow = e.trip?.trip_type === 'ReturnBooking'
    const isDraft = e.trip?.status === 'Draft'
    const isLocked = ['Completed', 'Cancelled', 'NoShow'].includes(e.trip?.status)
    const lockedLabel = e.trip?.status_label || e.trip?.status
    const [showPerksModal, setShowPerksModal] = useState(false)
    const [showMoreFaq, setShowMoreFaq] = useState(false)
    const [showPoliciesPopup, setShowPoliciesPopup] = useState(false)

    // Flow-specific FAQs, same structured set selectedCarDetailsLatest passes in the booking
    // flow. The values come from /trips/calculate-policies — NOT from the trip, which carries
    // no edit_policy / cancellation_policy on a Draft; useConfirmTrip runs that query. Read
    // from POLICY_CONSTANTS in the component body: they are cache-backed getters, empty until
    // the query resolves, so they must never be captured at module level.
    const faqs = getFaqsForFlow(e.tripDirection, {
        freeCancellationAvailable: POLICY_CONSTANTS.FREE_CANCELLATION_AVAILABLE,
        freeCancellationHours: POLICY_CONSTANTS.FREE_CANCELLATION_HOURS,
        freeEditsCount: POLICY_CONSTANTS.FREE_EDITS_COUNT,
        editableHoursBeforePickup: POLICY_CONSTANTS.EDITABLE_HOURS_BEFORE_PICKUP,
        waitingPickupFreeSeconds: POLICY_CONSTANTS.WAITING_PICKUP_FREE_SECONDS,
        waitingPickupRateCentsPerSecond: POLICY_CONSTANTS.WAITING_PICKUP_RATE_CENTS_PER_SECOND,
        waitingPickupMaxWaitSeconds: POLICY_CONSTANTS.WAITING_PICKUP_MAX_WAIT_SECONDS,
        cancellationText: POLICY_CONSTANTS.CANCELLATION_TEXT,
        modificationText: POLICY_CONSTANTS.MODIFICATION_TEXT,
    })

    // A Draft with saved cards and nothing chosen yet starts on the first card — the same
    // default the phone-booking page uses. Draft only: a live booking keeps the card it was made
    // with. Local state; tapping a card is what writes to the draft.
    const firstCardId = (e.savedCards?.length ? e.savedCards : (e.trip?.passenger?.payment_methods || []))[0]?.id
    useEffect(() => {
        if (isDraft && firstCardId != null && !e.selectedCardId) e.setSelectedCardId(firstCardId)
    }, [isDraft, firstCardId, e.selectedCardId]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        document.body.classList.add('ride-edit-page')
        return () => document.body.classList.remove('ride-edit-page')
    }, [])

    const renderSection = () => {
        switch (e.editingSection) {
            case 'destination': return <DestinationSection mode={e.destinationPart || 'all'} destinations={e.destinations} committedDestinations={e.committedDestinations} setDestinations={e.handleDestinationsChange} onDone={() => e.handleSectionDone('destination')} onClose={e.closeEdit} isLoading={e.isRecalculating} error={e.sectionSaveError}/>
            case 'car': return <CarSection availableCars={e.availableCars} selectedCar={e.selectedCar} committedCarTypeId={e.committedCarTypeId} setSelectedCar={(car) => { e.setSelectedCar(car) }} onDone={() => e.handleSectionDone('car')} onClose={e.closeEdit} isLoading={e.isRecalculating} isRecalculating={e.isRecalculating} error={e.sectionSaveError} driverTip={e.driverTip} setDriverTip={e.setDriverTip} onTipChange={e.handleTipChange} onCouponRecalculate={e.handleCouponRecalculate} defaultCouponCode={e.couponCode} breakdownCarId={e.carBreakdownId} setBreakdownCarId={e.setCarBreakdownId}/>
            case 'passengers': return <PassengerSection tripId={rideId} passengers={e.passengers} committedPassengers={e.committedPassengers} setPassengers={e.handlePassengersChange} childSeats={e.childSeats} committedChildSeats={e.committedChildSeats} setChildSeats={e.handleChildSeatsChange} passengerTypes={e.passengerTypes} childSeatTypes={e.childSeatTypes} onDone={() => e.handleSectionDone('passengers')} onClose={e.closeEdit} isLoading={e.isRecalculating} error={e.sectionSaveError} view={e.passengerView} onViewChange={e.setPassengerView} only={e.passengerOnly}/>
            case 'luggage': return <LuggageSection tripId={rideId} luggage={e.luggage} committedLuggage={e.committedLuggage} setLuggage={e.handleLuggageChange} oversizedLuggage={e.oversizedLuggage} committedOversizedLuggage={e.committedOversizedLuggage} setOversizedLuggage={e.handleOversizedChange} luggageTypes={e.luggageTypes} onDone={() => e.handleSectionDone('luggage')} onClose={e.closeEdit} isLoading={e.isRecalculating} error={e.sectionSaveError} oversizedMode={e.luggageOversizedMode} setOversizedMode={e.setLuggageOversizedMode}/>
            default: return null
        }
    }

    if (e.isLoading) {
        return <div className="py-5 pb-10 max-md:bg-(--bg-grey)"><SkeletonCards/></div>
    }

    if (!e.trip) {
        return <div className="py-5 pb-10"><div className="max-w-3xl mx-auto text-center p-4"><p className="text-(--text-secondary)">Trip not found</p></div></div>
    }

    return (
        <div className="py-5 pb-10 max-md:bg-(--bg-grey)">
            {/* Back sits at the left edge of the content column, vertically centred on
                the title block (which stays centred — hence absolute, not a flex row). */}
            <div className="max-w-3xl mx-auto text-center mb-5 relative px-14">
                <RideBackButton className="absolute left-2 top-1/2 -translate-y-1/2"/>
                {/* A DRAFT has never been confirmed, so "Edit" misnames what the page is
                    for — the passenger is completing a booking, not changing one. Both
                    flows read the same here; the return/one-way distinction matters when
                    editing an existing ride, not when finishing an unconfirmed one. */}
                <h1 className="font-bold text-2xl">
                    {isDraft ? 'Confirm Booking' : isReturnFlow ? 'Edit Return Booking' : 'Edit Booking'}
                </h1>
            </div>

            <div className="max-w-3xl mx-auto mb-5 flex flex-col gap-7">
                <div className="prebook-compare">
                    <ExclusivePerksBar onClick={() => setShowPerksModal(true)}/>
                </div>
                <div className="hopn-ride-latest">
                    <PopularQuestions faqs={faqs} onMore={() => setShowMoreFaq(true)}/>
                </div>
            </div>

            <div className="relative max-w-3xl mx-auto flex flex-col gap-4 p-5 pb-8 bg-(--bg-grey) rounded-(--radius-2xl) max-md:p-0 max-md:rounded-none max-md:bg-transparent">
                {isLocked && (
                    <div className="absolute inset-0 z-20 bg-white/70 backdrop-blur-sm rounded-(--radius-2xl) flex justify-center p-5" style={{paddingTop: '50%'}}>
                        <div className="bg-white rounded-(--radius-2xl) shadow-lg px-4 py-6 max-w-md text-center h-fit flex flex-col gap-3 items-center">
                            <h6 className="mb-0!">This trip is {lockedLabel} you can't modify this trip</h6>
                            {e.trip?.status === 'Cancelled' && e.trip?.cancelled_by && (
                                <span className="bg-(--bg-grey) px-4 py-2 rounded-full">Cancelled by {e.trip.cancelled_by}</span>
                            )}
                            {e.trip?.status === 'Cancelled' && e.trip?.cancellation_reason && (
                                <span><b>Reason:</b> {e.trip.cancellation_reason}</span>
                            )}
                            <a href={import.meta.env.VITE_BOOKING_PORTAL_URL} className="underline text-(--text-primary) font-medium">Create New Booking</a>
                        </div>
                    </div>
                )}

                {/* "Enter info to reserve return ride" — only on a RETURN booking. On a
                    first-leg draft there is no return ride to reserve, so the badge was
                    describing a trip the passenger isn't on. */}
                <RouteCard title={e.isFromAirport ? 'From Airport' : 'Start Ride Address'} summary="origin" showBanner={isReturnFlow} includeStopovers={e.isToAirport}
                           trip={{...e.trip, ...e.routeLocations, route: e.route, scheduled_at: e.scheduledAt}}
                           expanded={e.expanded.has('route')} onToggle={() => e.toggle('route')} onEdit={() => e.openEdit('destination', {part: e.isToAirport ? 'originStops' : 'origin'})}/>

                <RouteCard title="Drop off Address" summary="destination" showBanner={false} includeStopovers={!e.isToAirport}
                           trip={{...e.trip, ...e.routeLocations, route: e.route, scheduled_at: e.scheduledAt}}
                           expanded={e.expanded.has('dropoff')} onToggle={() => e.toggle('dropoff')} onEdit={() => e.openEdit('destination', {part: e.isToAirport ? 'destinationOnly' : 'dropoff'})}/>

                <DateTimeCard
                    trip={e.trip} parentScheduledAt={e.trip?.parent_trip?.scheduled_at}
                    isAirportTrip={e.isAirportTrip} hideFlightField={e.isToAirport}
                    isFromAirport={e.isFromAirport} isToAirport={e.isToAirport} onRecommend={() => e.setSmartTimePopup(true)}
                    flightNumber={e.sharedFlightNumber}
                    onFlightNumberChange={e.handleFlightNumberChange}
                    onFlightClear={e.clearFlight} flightAdded={e.hasFlightNumber}
                    onSmartTime={e.handleFlightAdd}
                    applySmartTimeRef={e.applySmartTimeRef}
                    onDateTimeChange={e.setLiveDatetime} onDateChange={e.handleDateChange} onTimeChange={e.handleTimeChange}
                    dateHint={e.dateHint} setDateHint={(v) => { e.setDateHint(v); if (!v && e.isFromAirport) e.setFlightHint(true) }}
                    dateHintText="Please set the date"
                    dateTimeSectionRef={e.dateTimeSectionRef}
                    leadTimeError={e.minLeadTimeError}
                    saving={e.isSavingDateTime}
                />

                <PassengersCard trip={e.trip} expanded={e.expanded.has('passengers')} onToggle={() => e.toggle('passengers')} onEdit={() => e.openEdit('passengers', {passengerOnly: 'passengers'})} policy={e.getFieldPolicy('passenger_types')}/>

                <ChildSeatsCard trip={e.trip} expanded={e.expanded.has('childseats')} onToggle={() => e.toggle('childseats')} onEdit={() => e.openEdit('passengers', {passengerOnly: 'childseats'})} policy={e.getFieldPolicy('child_seats')}/>

                <LuggageItemsCard trip={e.trip} expanded={e.expanded.has('luggage')} onToggle={() => e.toggle('luggage')} onEdit={() => e.openEdit('luggage')} policy={e.getSectionPolicy('luggage')}/>

                <OversizedCard trip={e.trip} expanded={e.expanded.has('oversized')} onToggle={() => e.toggle('oversized')} onEdit={() => e.openEdit('luggage', {oversized: true})} policy={e.getSectionPolicy('luggage')}/>

                <div className="flex gap-4 items-stretch max-md:flex-col">
                    <SpecialRequestCard trip={e.trip} specialRequest={e.specialRequest} contact={e.contact} onEditSpecialRequest={() => e.setShowSRPopup(true)}/>
                    {/* This page NEVER shows a server-side fare — a Draft has no price and no
                        selected_vehicle. The card renders OUR calculate-route → calculate-cost
                        result, which now runs on load (and again on any date/time change), so
                        there is no placeholder state: while a quote is in flight the card shows
                        its own loader off isRecalculating. */}
                    <CarDetailsCard trip={e.trip} driverTip={e.driverTip} setDriverTip={e.setDriverTip} onTipChange={e.handleTipChange} isRecalculating={e.isRecalculating} onEditCar={() => e.openEdit('car')}/>
                </div>

                <PaymentMethodsCard trip={e.trip} savedCards={e.savedCards} selectedCardId={e.selectedCardId} setSelectedCardId={e.handleCardSelect} onCardAdded={(c) => e.setSavedCards(prev => [...prev, c])} walletAmountCents={e.trip?.wallet_amount_cents}/>

                {e.identityMissing && <IdentityIncompleteNotice missing={e.identityMissing} className="mb-3" onCompleted={() => e.setIdentityMissing(null)}/>}
                {e.bookingError && <p className="text-red-500 text-center text-sm">{e.bookingError}</p>}

                <div className="relative">
                    {(() => {
                        // A schedule-less draft commits no date/time until the user picks them
                        // (see DateTimeCard) — require both before confirming. Falling back to
                        // trip.scheduled_at avoids a disabled flash before the mount-commit lands.
                        const hasDate = !!e.liveDatetime?.date || !!e.trip?.scheduled_at
                        const hasTime = !!e.liveDatetime?.time || !!e.trip?.scheduled_at
                        // Gate on the flight NUMBER, NOT the sharedFlightDetails lookup object
                        // (else an existing flight is falsely gated). Computed in useEditTrip so a
                        // direction flip that drops the flight is reflected here at runtime.
                        const hasFlightNumber = e.hasFlightNumber
                        // The payment leg is load-bearing: no card is pre-selected any more
                        // (see the payment hydration in useConfirmTrip), so without it a
                        // passenger who never taps a card submits `payment_method_id: null`
                        // and /confirm 422s for "at least one payment source".
                        const gateMessage = !hasDate ? 'Please select the arrival date to continue'
                            : (e.isFromAirport && !hasFlightNumber) ? 'Please enter your flight number to continue'
                            : !hasTime ? 'Please select the arrival time to continue'
                            : !e.selectedCardId ? 'Please select a payment method to continue'
                            : null
                        return (
                            <>
                                <button type="button" onClick={e.doConfirm} disabled={e.isSubmitting || !!gateMessage || !!e.minLeadTimeError}
                                        className="hopn-add-passenger-button">
                                    Continue
                                </button>
                                {gateMessage && (
                                    <div className="absolute top-full right-0 mt-3 bg-[#3c393c] text-white rounded-xl p-3 text-center text-sm z-10 max-w-[280px] after:content-[''] after:absolute after:w-0 after:h-0 after:border-l-24 after:border-l-transparent after:border-r-[9px] after:border-r-transparent after:border-b-20 after:border-b-[#3c393c] after:-top-4.75 after:right-7.5">
                                        <p className="mb-0!">{gateMessage}</p>
                                    </div>
                                )}
                            </>
                        )
                    })()}
                </div>

                <p className="mb-0 text-sm text-center cursor-pointer underline" onClick={() => setShowPoliciesPopup(true)}>By proceeding, you confirm that you understand and agree to these terms.</p>
            </div>

            {/* Smart Time Popup */}
            {/* flushBody/flushTop because the screens bring their own spacing: their
                `.modalPages-spacing` gutters, and their own ModalHeader as the first
                element — the Modal's px-8 and headerless top padding sat on top of both. */}
            <Modal isOpen={e.smartTimePopup} onClose={e.closeSmartTime} className="md:max-w-157.5" flushBody flushTop>
                {/* The booking flow's recommended-pickup screens (chooser -> recommendation).
                    They render their own headers, so no ModalHeader here — the outer
                    "Smart Time Select" title belonged to the DateTimeSection this replaced. */}
                <SmartPickupFlow
                    destinations={[
                        e.committedDestinations?.origin,
                        e.committedDestinations?.destination,
                        ...(e.committedDestinations?.stopOvers || []),
                    ].filter(Boolean)}
                    rideDate={e.liveDatetime.date}
                    routeData={e.route}
                    flightNumber={e.sharedFlightNumber}
                    flightDetails={e.sharedFlightDetails}
                    onFlightChange={({flightNumber, flightDetails}) => {
                        e.setSharedFlightNumber(flightNumber);
                        e.setSharedFlightDetails(flightDetails);
                    }}
                    onSelectTime={e.handleSmartTimeSelect}
                    onClose={e.closeSmartTime}
                />
            </Modal>

            {/* Section Edit Modal */}
            <Modal isOpen={!!e.editingSection} onClose={e.closeEdit}>
                <ModalHeader
                    title={
                        e.passengerView === 'seatDetails' && e.editingSection === 'passengers' ? 'Found Right Seat'
                        : e.luggageOversizedMode && e.editingSection === 'luggage' ? 'Add Oversized'
                        : e.carBreakdownId && e.editingSection === 'car' ? 'Payment'
                        : e.editingSection === 'destination' ? ((e.destinationPart === 'origin' || e.destinationPart === 'originStops') ? (e.isFromAirport ? 'From Airport' : 'Start Ride Address') : (e.destinationPart === 'dropoff' || e.destinationPart === 'destinationOnly') ? 'Drop off Address' : 'Pick-up & Drop-off')
                        : e.editingSection === 'car' ? 'Select Vehicle'
                        : e.editingSection === 'passengers' ? (e.passengerOnly === 'childseats' ? 'Child Seats' : e.passengerOnly === 'passengers' ? 'Passengers' : 'Passengers & Child Seats')
                        : e.editingSection === 'luggage' ? 'Luggage'
                        : ''
                    }
                    onBack={
                        (e.passengerView === 'seatDetails' && e.editingSection === 'passengers') ? () => e.setPassengerView('main')
                        : (e.carBreakdownId && e.editingSection === 'car') ? () => e.setCarBreakdownId(null)
                        : undefined
                    }
                    onClose={
                        (e.carBreakdownId && e.editingSection === 'car')
                            ? undefined : e.closeEdit
                    }
                />
                {renderSection()}
            </Modal>

            <SpecialRequestPopup isOpen={e.showSRPopup} onClose={() => e.setShowSRPopup(false)} specialRequest={e.specialRequest} onSave={e.handleSpecialRequestSave}/>
            <FlightDelayedPopup isOpen={e.infoPopup === 'flightDelayed'} onClose={() => e.setInfoPopup(null)}/>
            <LostBaggagePopup isOpen={e.infoPopup === 'lostBaggage'} onClose={() => e.setInfoPopup(null)}/>
            <AirportPickupPerksPopup isOpen={e.infoPopup === 'morePerks'} onClose={() => e.setInfoPopup(null)}/>
            <ComingSoonPopup isOpen={e.infoPopup === 'findDriver'} onClose={() => e.setInfoPopup(null)} title="Find Your Driver"/>
            <SuccessPopup isOpen={e.showSuccess} isReturnFlow={isReturnFlow} trip={e.trip} onDone={() => navigate('/')}/>
            <PoliciesPopup isOpen={showPoliciesPopup} onClose={() => setShowPoliciesPopup(false)} trip={e.trip} fromCalculatedPolicies/>

            {/* Exclusive Airport Pickup Perks — perks list + nested perk detail views */}
            <AirportPerksModal isOpen={showPerksModal} onClose={() => setShowPerksModal(false)} trip={e.trip} fromCalculatedPolicies/>

            {/* "More" popular questions (the rest of POPULAR_QUESTIONS) */}
            <Modal isOpen={showMoreFaq} onClose={() => setShowMoreFaq(false)}>
                <ModalHeader title="Popular questions" onClose={() => setShowMoreFaq(false)}/>
                <div className="hopn-ride-latest">
                    <div className="hopn-ride-faq">
                        <FaqAccordionList items={faqs.slice(FAQ_INLINE_COUNT)}/>
                    </div>
                </div>
            </Modal>
        </div>
    )
}
